import { Spinner, TextField, Typography } from '@compass/components'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { AppRoute, buildRoute } from 'appRoutes'
import {
  QueryFilterCustomUIRenderProps,
  QueryFilterDefinition,
  QueryFilterItem,
  QueryFilterValues,
} from 'components/QueryFilters'
import { useCurrentSubscription } from 'features/subscription'
import { useEventQueryV4 } from 'hooks/api/event'
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { useFilterSearchParams } from '../../hooks/useFilterSearchParams'
import { VisitsFilterView } from '../../types'

export type VisitHistoryFilterItemProps = QueryFilterDefinition<VisitsFilterView> & {
  className?: string
  autoFocus?: boolean
  isOpen?: boolean
  setIsOpen?: (isOpen: boolean) => void
  value?: string | number | string[] | boolean
}

export function VisitHistoryFilterItem({
  className,
  autoFocus,
  isOpen,
  setIsOpen,
  ...props
}: VisitHistoryFilterItemProps) {
  const { filterValues, setSearchParams } = useFilterSearchParams()

  return (
    <QueryFilterItem
      filter={props}
      filterValues={filterValues}
      onChange={setSearchParams}
      className={className}
      autoFocus={autoFocus}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      value={props.value}
      testId='visit-history-filter-item'
    />
  )
}

export function RequestIdCustomFilterUI<TValues extends QueryFilterValues>({
  filter,
  value: defaultValue = '',
  autoFocus,
  setIsOpen,
}: QueryFilterCustomUIRenderProps<TValues>) {
  const navigate = useNavigate()
  const search = useLocation({ select: (location) => location.searchStr })
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const [controlledValue, setControlledValue] = useState(defaultValue as string)
  const [isFetchError, setIsFetchError] = useState(false)
  const [requestId, setRequestId] = useState('')

  const query = useEventQueryV4({
    subscriptionId,
    requestId,
    enabled: !!requestId,
  })

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.code === 'Enter') {
        if (typeof filter.validate?.(controlledValue) === 'string') {
          setIsFetchError(false)
          return
        }

        setRequestId(controlledValue)
      }

      if (evt.code === 'Escape') {
        setControlledValue(defaultValue as string)
        setIsOpen?.(false)
      }
    },
    [controlledValue, defaultValue, filter, setIsOpen]
  )

  const hasNavigatedRef = useRef(false)

  useEffect(() => {
    if (hasNavigatedRef.current) {
      return
    }

    if (query.isError) {
      setIsFetchError(true)
      return
    }

    if (query.isSuccess && query.data) {
      hasNavigatedRef.current = true

      navigate({
        to: buildRoute(AppRoute.IdentificationEventDetails, {
          subscriptionId,
          requestId,
        }),
        state: { search },
      })
    }
  }, [navigate, query.data, query.isError, query.isSuccess, requestId, search, subscriptionId])

  return (
    <div className='px-3 py-2' data-testid='visit-history-filter-item' data-filter-type='text'>
      <TextField
        name={filter.id}
        aria-labelledby={filter.id + '-label'}
        aria-describedby={filter.id + '-description'}
        label={() => (
          <Typography id={`${filter.id}-label`} as='label' size='xs' className='text-gray-800'>
            <strong>{filter.label}</strong> is
          </Typography>
        )}
        description={() => (
          <Typography id={`${filter.id}-description`} as='div' size='xs' className='text-gray-800'>
            {filter.description}
          </Typography>
        )}
        suffix={query.isFetching && <Spinner aria-label='Loading' />}
        value={controlledValue}
        type='text'
        placeholder={filter.placeholder}
        onChange={setControlledValue}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        validate={filter.validate}
        isRequired
        {...(isFetchError && { isInvalid: true })}
        {...(isFetchError && { errorMessage: 'No event found with this Event ID' })}
      />
    </div>
  )
}
